import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { api, fileUrl } from "@/lib/api";

/* =====================================================================
   CAMPAIGN REEL — horizontal photo carousel managed via /admin.
   Renders one reel per `category` (damp / crimes / jokes).
   - Snap-scroll on touch, arrow buttons on desktop
   - Hidden entirely if the reel has no photos yet
   ===================================================================== */
export default function CampaignReel({
  category = "damp",
  kicker = "",
  title = "",
  compact = false,
}) {
  const [photos, setPhotos] = useState([]);
  const scrollerRef = useRef(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  useEffect(() => {
    api
      .get("/homepage-photos", { params: { category } })
      .then((r) => setPhotos(r.data || []))
      .catch(() => {});
  }, [category]);

  useEffect(() => {
    updateEdgeState();
  }, [photos]);

  function updateEdgeState() {
    const el = scrollerRef.current;
    if (!el) return;
    setAtStart(el.scrollLeft <= 4);
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 4);
  }

  function scrollBy(direction) {
    const el = scrollerRef.current;
    if (!el) return;
    // Scroll roughly one tile-width at a time so the snap behaviour feels
    // deliberate rather than jumping a full viewport.
    const tile = el.querySelector("[data-tile]");
    const step = tile ? tile.getBoundingClientRect().width + 16 : el.clientWidth * 0.6;
    el.scrollBy({ left: direction * step, behavior: "smooth" });
  }

  if (photos.length === 0) return null;

  return (
    <section
      className={`bg-[#F9F9F4] ${compact ? "pb-8 pt-0 md:pb-12" : "pb-16 pt-0 md:pb-20"}`}
      data-testid={`campaign-reel-${category}`}
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div
          className={`flex items-baseline justify-between gap-4 ${
            kicker || title ? "mb-8 md:mb-12" : "mb-4 justify-end md:mb-6"
          }`}
        >
          {(kicker || title) && (
            <div>
              {kicker && (
                <div className="font-display text-[10px] font-bold uppercase tracking-[0.4em] text-[#1A3626]/70 md:text-xs">
                  {kicker}
                </div>
              )}
              {title && (
                <h2 className="font-display mt-3 text-3xl font-black tracking-tight text-[#1A3626] md:text-5xl">
                  {title}
                </h2>
              )}
            </div>
          )}
          <div className="hidden items-center gap-2 md:flex">
            <button
              type="button"
              onClick={() => scrollBy(-1)}
              disabled={atStart}
              className="font-display flex h-12 w-12 items-center justify-center border-2 border-[#1A3626] text-[#1A3626] transition-all hover:bg-[#1A3626] hover:text-[#F9F9F4] disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-[#1A3626]"
              data-testid="reel-scroll-left"
              aria-label="Scroll reel left"
            >
              <ArrowLeft className="h-5 w-5" strokeWidth={2.4} />
            </button>
            <button
              type="button"
              onClick={() => scrollBy(1)}
              disabled={atEnd}
              className="font-display flex h-12 w-12 items-center justify-center border-2 border-[#1A3626] text-[#1A3626] transition-all hover:bg-[#1A3626] hover:text-[#F9F9F4] disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-[#1A3626]"
              data-testid="reel-scroll-right"
              aria-label="Scroll reel right"
            >
              <ArrowRight className="h-5 w-5" strokeWidth={2.4} />
            </button>
          </div>
        </div>
      </div>

      <div
        ref={scrollerRef}
        onScroll={updateEdgeState}
        className="reel-scroller flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth pb-4 pl-6 pr-2 md:gap-5 md:pl-10 md:pr-4"
        style={{ scrollbarWidth: "none", scrollPaddingLeft: "1.5rem" }}
        data-testid="reel-scroller"
      >
        {photos.map((p) => (
          <ReelTile key={p.id} photo={p} compact={compact} />
        ))}
        {/* End spacer so the last tile snaps cleanly without bumping the right edge */}
        <div className="w-2 shrink-0 md:w-6" aria-hidden />
      </div>
    </section>
  );
}

// Each tile is wrapped in a clickable container when the curator has set a
// link_url on the photo. Internal links (starting with "/") use react-router;
// external links open in a new tab.
function ReelTile({ photo: p, compact = false }) {
  const sharedClass = compact
    ? "group flex w-[60%] shrink-0 snap-start flex-col sm:w-[40%] md:w-[28%] lg:w-[22%] xl:w-[18%]"
    : "group flex w-[78%] shrink-0 snap-start flex-col sm:w-[55%] md:w-[42%] lg:w-[30%] xl:w-[24%]";
  const tileBody = (
    <>
      <div className="aspect-[3/4] overflow-hidden bg-[#EBE7DD]">
        <img
          src={fileUrl(p.image_url)}
          alt={p.label}
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
          loading="lazy"
        />
      </div>
      <div className={`mt-4 font-display font-bold uppercase tracking-[0.28em] text-[#1A3626] ${compact ? "text-[10px] md:text-xs md:tracking-[0.3em]" : "text-xs md:text-sm md:tracking-[0.32em]"}`}>
        {p.label}
      </div>
    </>
  );

  if (!p.link_url) {
    return (
      <article data-tile className={sharedClass} data-testid={`reel-tile-${p.id}`}>
        {tileBody}
      </article>
    );
  }

  const isExternal = /^https?:\/\//i.test(p.link_url);
  if (isExternal) {
    return (
      <a
        data-tile
        href={p.link_url}
        target="_blank"
        rel="noopener noreferrer"
        className={sharedClass}
        data-testid={`reel-tile-${p.id}`}
      >
        {tileBody}
      </a>
    );
  }
  return (
    <Link
      data-tile
      to={p.link_url}
      className={sharedClass}
      data-testid={`reel-tile-${p.id}`}
    >
      {tileBody}
    </Link>
  );
}
